import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Center, Checkbox, Collapse, Group, Loader, Modal, ScrollArea, Stack, Table, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, scheduleApi } from '../../api';
import { formatDate, todayIso } from '../../format';
import { calendarOf } from './localSchedule';
import { type ClaudeChange, NEW_PREFIX, buildRoll, previewRoll, rollForward, rollWarnings } from './rollforward';

const CHANGE_LABEL: Record<ClaudeChange['type'], string> = { duration: 'Dauer', start: 'Beginn', new: 'neuer Vorgang', link: 'Abhängigkeit' };

function describe(c: ClaudeChange): string {
  if (c.type === 'duration') return `${c.code}: ${c.days} Arbeitstage`;
  if (c.type === 'start') return `${c.code}: frühestens ${formatDate(c.date)}`;
  if (c.type === 'new') return `${c.code} ${c.name}, ${c.days} Arbeitstage${c.after.length ? `, nach ${c.after.join(', ')}` : ''}`;
  return `${c.to} erst nach ${c.from}`;
}

/**
 * Auf heute fortschreiben: the fixed rule as a preview (done stays, running
 * goes on, open starts today at the earliest), optionally with what Claude
 * reads from new information - each change to tick or untick -, and taking
 * it over as the plan. Before that PST keeps the plan as it was: a snapshot
 * to go back to, a baseline to compare against.
 */
export function RollforwardModal({ projectId, opened, onClose, canTakeOver }: {
  projectId: string;
  opened: boolean;
  onClose: () => void;
  /** Taking over sets a baseline, so it needs the baseline right. */
  canTakeOver: boolean;
}): JSX.Element {
  return (
    <Modal opened={opened} onClose={onClose} size="xl" title="Terminplan auf heute fortschreiben">
      {opened && <Body projectId={projectId} onClose={onClose} canTakeOver={canTakeOver} />}
    </Modal>
  );
}

function Body({ projectId, onClose, canTakeOver }: { projectId: string; onClose: () => void; canTakeOver: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const plan = useQuery({ queryKey: ['plan', projectId], queryFn: () => scheduleApi.plan(projectId) });
  const schedule = useQuery({ queryKey: ['schedule', projectId], queryFn: () => scheduleApi.schedule(projectId) });
  const deps = useQuery({ queryKey: ['deps', projectId], queryFn: () => scheduleApi.dependencies(projectId) });
  const claude = useQuery({
    queryKey: ['rollforward', projectId],
    queryFn: () => scheduleApi.rollforward(projectId),
    refetchInterval: (q) => (q.state.data?.pending ? 6000 : false),
  });
  const [status, setStatus] = useState(todayIso());
  const [info, setInfo] = useState('');
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [showRule, setShowRule] = useState(false);
  const [baselineName, setBaselineName] = useState(`Stand vor Fortschreibung ${formatDate(todayIso())}`);
  // A new proposal: everything ticked to begin with.
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const key = JSON.stringify(claude.data?.changes ?? null);
    if (key !== seen.current) { seen.current = key; setChosen(new Set((claude.data?.changes ?? []).map((_, i) => i))); }
  }, [claude.data?.changes]);
  // The text Claude was last given comes back once, when the window opens - not every time the field is empty.
  const filled = useRef(false);
  useEffect(() => {
    if (filled.current || !claude.data) return;
    filled.current = true;
    if (claude.data.info) setInfo(claude.data.info);
  }, [claude.data]);

  const ask = useMutation({
    mutationFn: () => scheduleApi.askRollforward(projectId, { info: info.trim(), statusDate: status }),
    onSuccess: (st) => queryClient.setQueryData(['rollforward', projectId], st),
  });
  const discard = useMutation({
    mutationFn: () => scheduleApi.discardRollforward(projectId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rollforward', projectId] }),
  });

  const calendar = useMemo(() => (plan.data ? calendarOf(plan.data) : null), [plan.data]);
  const rule = useMemo(() => (schedule.data && calendar && /^\d{4}-\d{2}-\d{2}$/.test(status) ? rollForward(schedule.data.tasks, status, calendar) : []), [schedule.data, calendar, status]);
  const picked = useMemo(() => (claude.data?.changes ?? []).filter((_, i) => chosen.has(i)), [claude.data?.changes, chosen]);
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(status);
  const roll = useMemo(
    () => (schedule.data && deps.data && calendar && valid ? buildRoll(schedule.data, deps.data, rule, picked, status, calendar) : null),
    [schedule.data, deps.data, calendar, rule, picked, status, valid],
  );
  const preview = useMemo(
    () => (plan.data && schedule.data && deps.data && calendar && roll ? previewRoll(plan.data, schedule.data, deps.data, roll, calendar) : null),
    [plan.data, schedule.data, deps.data, calendar, roll],
  );
  const warnings = useMemo(() => (schedule.data && deps.data ? rollWarnings(schedule.data.tasks, deps.data) : []), [schedule.data, deps.data]);

  const apply = useMutation({
    mutationFn: () => scheduleApi.applyRollforward(projectId, { statusDate: status, baselineName: baselineName.trim(), ...roll! }),
    onSuccess: async () => {
      await Promise.all([['schedule', projectId], ['deps', projectId], ['plan', projectId], ['baseline', projectId], ['baselines', projectId], ['rollforward', projectId], ['agenda']]
        .map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      onClose();
    },
  });

  if (plan.isPending || schedule.isPending || deps.isPending) return <Center h={160}><Loader size="sm" /></Center>;
  const err = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.message : fallback);
  const shift = preview ? Math.round((Date.parse(preview.finishAfter) - Date.parse(preview.finishBefore)) / 86_400_000) : 0;
  const nothing = !roll || roll.updates.length + roll.newTasks.length + roll.links.length === 0;

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Erledigte Vorgänge bleiben auf ihren Terminen (bis zum Tag „erledigt am“), laufende laufen ab ihrem Beginn weiter und enden frühestens am Stichtag, offene beginnen frühestens am Stichtag. Das Ergebnis ist zunächst nur eine Vorschau.
      </Text>
      <Group gap="sm" align="flex-end">
        <TextInput type="date" label="Stichtag" value={status} onChange={(e) => setStatus(e.currentTarget.value)} w={170} />
        <Text size="sm" pb={6}>
          {rule.length === 0 ? 'Nach der Regel ändert sich nichts.' : `${rule.length} Vorgänge nach der Regel angepasst.`}{' '}
          {rule.length > 0 && <Text span size="sm" c="brand" style={{ cursor: 'pointer' }} td="underline" onClick={() => setShowRule((v) => !v)}>{showRule ? 'ausblenden' : 'zeigen'}</Text>}
        </Text>
      </Group>
      <Collapse in={showRule}>
        <ScrollArea.Autosize mah={160}>
          <Stack gap={2}>{rule.map((c) => <Text key={c.taskId} size="xs"><Text span ff="monospace" fw={600}>{c.code}</Text> {c.name} – {c.reason}</Text>)}</Stack>
        </ScrollArea.Autosize>
      </Collapse>

      <Stack gap={6} p="sm" style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-sm)' }}>
        <Group justify="space-between">
          <Text size="sm" fw={600}>Neue Informationen (optional, mit Claude)</Text>
          {claude.data?.available === false && <Badge size="xs" color="gray" variant="light">KI am Server gerade nicht erreichbar</Badge>}
        </Group>
        <Textarea
          autosize minRows={2} maxRows={8} maxLength={8000} value={info} onChange={(e) => setInfo(e.currentTarget.value)}
          placeholder="z. B. „Das Bauamt braucht voraussichtlich 6 Wochen länger. Die Statik kommt erst am 20.11. Vor dem Bauantrag ist eine Abstimmung mit dem Denkmalschutz nötig.“"
        />
        <Group gap="xs">
          <Button size="xs" variant="light" onClick={() => ask.mutate()} loading={ask.isPending || claude.data?.pending} disabled={info.trim().length < 10 || claude.data?.available === false}>
            Claude Änderungen vorschlagen lassen
          </Button>
          {(claude.data?.changes?.length ?? 0) > 0 && <Button size="xs" variant="subtle" color="gray" onClick={() => discard.mutate()}>Vorschlag verwerfen</Button>}
          {claude.data?.pending && <Text size="xs" c="dimmed">Claude liest – meist ein bis wenige Minuten.</Text>}
        </Group>
        {ask.isError && <Alert color="red" py={4}>{err(ask.error, 'Claude konnte nicht beauftragt werden.')}</Alert>}
        {claude.data?.note && !claude.data.pending && <Text size="xs" c="dimmed">{claude.data.note}</Text>}
        {(claude.data?.changes ?? []).map((c, i) => (
          <Checkbox
            key={i} size="xs" checked={chosen.has(i)}
            onChange={(e) => { const on = e.currentTarget.checked; setChosen((prev) => { const next = new Set(prev); if (on) next.add(i); else next.delete(i); return next; }); }}
            label={<Text size="xs"><Badge size="xs" variant="light" mr={6}>{CHANGE_LABEL[c.type]}</Badge>{describe(c)} <Text span size="xs" c="dimmed">– {c.reason}</Text></Text>}
          />
        ))}
      </Stack>

      {preview === null ? (
        <Alert color="red" py={6}>Die Vorschau lässt sich nicht rechnen – vermutlich schließt eine vorgeschlagene Abhängigkeit einen Kreis. Bitte Vorschläge abwählen.</Alert>
      ) : (
        <Stack gap={6}>
          <Text size="sm">
            Projektende <b>{formatDate(preview.finishBefore)}</b> → <b>{formatDate(preview.finishAfter)}</b>
            {shift !== 0 && <Text span size="sm" c={shift > 0 ? 'red.7' : 'green.7'}> ({shift > 0 ? '+' : ''}{shift} Kalendertage)</Text>}
            {' · '}{preview.moved.length} Vorgänge mit neuen Terminen
          </Text>
          {roll && roll.skipped.length > 0 && <Text size="xs" c="orange.8">Nicht verwendet: {roll.skipped.join('; ')}</Text>}
          {warnings.length > 0 && <Text size="xs" c="orange.8">Bitte ansehen: {warnings.join('; ')}</Text>}
          {preview.moved.length > 0 && (
            <ScrollArea.Autosize mah={240}>
              <Table verticalSpacing={2} className="pst-tabular" layout="fixed">
                <Table.Thead><Table.Tr><Table.Th w={70}>Nr.</Table.Th><Table.Th>Vorgang</Table.Th><Table.Th w={190}>Beginn</Table.Th><Table.Th w={190}>Ende</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {preview.moved.map((m) => (
                    <Table.Tr key={m.id}>
                      <Table.Td><Text size="xs" ff="monospace">{m.code}</Text></Table.Td>
                      <Table.Td><Text size="xs" truncate title={m.name}>{m.id.startsWith(NEW_PREFIX) ? <Badge size="xs" variant="light" mr={4}>neu</Badge> : null}{m.name}</Text></Table.Td>
                      <Table.Td><Text size="xs">{m.fromStart ? `${formatDate(m.fromStart)} → ` : ''}{formatDate(m.toStart)}</Text></Table.Td>
                      <Table.Td><Text size="xs">{m.fromFinish ? `${formatDate(m.fromFinish)} → ` : ''}{formatDate(m.toFinish)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          )}
        </Stack>
      )}

      <TextInput size="xs" label="Bisheriger Stand wird festgehalten als" description="als Basisplan zum Vergleich; dazu ein Snapshot zum Zurückholen" value={baselineName} onChange={(e) => setBaselineName(e.currentTarget.value)} maxLength={120} />
      {apply.isError && <Alert color="red" py={6}>{err(apply.error, 'Übernehmen fehlgeschlagen.')}</Alert>}
      {!canTakeOver && <Text size="xs" c="dimmed">Übernehmen darf die Projektleitung (es legt einen Basisplan an).</Text>}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>Schließen</Button>
        <Button onClick={() => apply.mutate()} loading={apply.isPending} disabled={!canTakeOver || nothing || preview === null}>Als Terminplan übernehmen</Button>
      </Group>
    </Stack>
  );
}
